import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { DatePicker } from "@/components/DatePicker";
import { ChevronLeft, Archive, RefreshCw, XCircle, AlertTriangle, RefreshCcw } from "lucide-react";

const iso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export default function CommissionsCollected() {
  const navigate = useNavigate();
  const now = new Date();
  const defaultStart = iso(new Date(now.getFullYear(), 0, 1));
  const defaultEnd = iso(now);

  const [startCollected, setStartCollected] = useState(defaultStart);
  const [endCollected, setEndCollected] = useState(defaultEnd);
  const [startDue, setStartDue] = useState("");
  const [endDue, setEndDue] = useState("");
  const [brokerFilter, setBrokerFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [groupBy, setGroupBy] = useState("broker");

  const [brokers, setBrokers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/brokers").then(({ data }) => setBrokers(data || [])).catch(() => {});
    api.get("/companies").then(({ data }) => setCompanies(data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { group_by: groupBy };
      if (startCollected) params.start_date_collected = startCollected;
      if (endCollected) params.end_date_collected = endCollected;
      if (startDue) params.start_date_due = startDue;
      if (endDue) params.end_date_due = endDue;
      if (brokerFilter !== "all") params.broker_id = brokerFilter;
      if (companyFilter !== "all") params.company_id = companyFilter;
      const { data } = await api.get("/commissions/collected", { params });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [startCollected, endCollected, startDue, endDue, brokerFilter, companyFilter, groupBy]);

  useEffect(() => { load(); }, [load]);

  const uncollect = useCallback(async (policyId, installmentDate) => {
    if (!window.confirm("Annullare l'incasso di questa provvigione?")) return;
    try {
      await api.post(`/policies/${policyId}/installments/${installmentDate}/uncollect-commission`);
      toast.success("Incasso annullato.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [load]);

  // 🆕 22/07/2026 — Ricalcolo puntuale di un singolo incasso disallineato.
  const recalcOne = useCallback(async (policyId, installmentDate) => {
    if (!window.confirm("Ricalcolare l'importo di questo incasso con la formula corrente?\nData e note vengono preservate.")) return;
    try {
      const { data: res } = await api.post(`/policies/${policyId}/installments/${installmentDate}/recalc-commission`);
      const diff = Number(res.diff || 0);
      const sign = diff >= 0 ? "+" : "";
      toast.success(`Importo aggiornato: ${formatCurrency(res.previous_amount)} → ${formatCurrency(res.new_amount)} (${sign}${diff.toFixed(2).replace(".", ",")} €)`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [load]);

  // 🆕 22/07/2026 — Ricalcolo bulk di TUTTI gli incassi disallineati nei filtri correnti.
  const recalcAllMismatch = useCallback(async () => {
    if (!data?.rows?.length) return;
    const mismatches = data.rows.filter((r) => r.has_mismatch);
    if (mismatches.length === 0) { toast.info("Nessun incasso disallineato."); return; }
    if (!window.confirm(`Ricalcolare l'importo di ${mismatches.length} incass${mismatches.length === 1 ? "o" : "i"} disallineat${mismatches.length === 1 ? "o" : "i"}?`)) return;
    let ok = 0, err = 0;
    for (const r of mismatches) {
      try {
        await api.post(`/policies/${r.policy_id}/installments/${r.installment_date}/recalc-commission`);
        ok += 1;
      } catch { err += 1; }
    }
    if (ok && !err) toast.success(`${ok} incass${ok === 1 ? "o aggiornato" : "i aggiornati"}.`);
    else if (ok && err) toast.warning(`${ok} aggiornati, ${err} errori.`);
    else toast.error(`Nessuno aggiornato. ${err} errori.`);
    await load();
  }, [data, load]);

  const exportCsv = useCallback(() => {
    if (!data?.rows?.length) { toast.info("Nessuna riga da esportare."); return; }
    const header = ["Data incasso", "Scadenza rata", "Cliente", "Polizza", "Compagnia", "Broker", "Importo (€)", "Note"];
    const rows = data.rows.map((r) => [
      r.collected_at || "",
      r.installment_date || "",
      r.client_name || "",
      r.policy_number || "",
      r.company_name || "",
      r.broker_name || "",
      String((r.amount_collected || 0).toFixed(2)).replace(".", ","),
      (r.note || "").replace(/[\r\n]/g, " "),
    ]);
    const csv = [header, ...rows].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\r\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `provvigioni-incassate_${iso(new Date())}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [data]);

  const totalLabel = useMemo(() => {
    if (!data) return "";
    return `${data.count} ${data.count === 1 ? "riga" : "righe"} · ${formatCurrency(data.total_amount)}`;
  }, [data]);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="commissions-collected-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-700 mb-1">Archivio</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-2">
            <Archive className="h-8 w-8 text-emerald-600" /> Provvigioni Incassate
          </h1>
          <p className="text-slate-500 mt-1">Storico delle provvigioni segnate come incassate, con filtri per periodo, broker/agenzia e compagnia.</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            data-testid="back-to-statement"
            onClick={() => navigate("/commissions/statement")}
            className="border-slate-300"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Torna a Estratto conto
          </Button>
          <Button
            data-testid="reload-btn"
            onClick={load}
            variant="outline"
            className="border-slate-300"
          >
            <RefreshCw className="h-4 w-4 mr-1" /> Ricarica
          </Button>
          <Button
            data-testid="export-csv"
            onClick={exportCsv}
            variant="outline"
            className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
            disabled={!data?.rows?.length}
          >
            📄 Esporta CSV
          </Button>
        </div>
      </header>

      {/* Filtri */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <div>
            <Label className="text-xs">Incasso — dal</Label>
            <DatePicker value={startCollected} onChange={(e) => setStartCollected(e?.target?.value || "")} data-testid="filter-start-collected" />
          </div>
          <div>
            <Label className="text-xs">Incasso — al</Label>
            <DatePicker value={endCollected} onChange={(e) => setEndCollected(e?.target?.value || "")} data-testid="filter-end-collected" />
          </div>
          <div>
            <Label className="text-xs">Scadenza rata — dal</Label>
            <DatePicker value={startDue} onChange={(e) => setStartDue(e?.target?.value || "")} data-testid="filter-start-due" />
          </div>
          <div>
            <Label className="text-xs">Scadenza rata — al</Label>
            <DatePicker value={endDue} onChange={(e) => setEndDue(e?.target?.value || "")} data-testid="filter-end-due" />
          </div>
          <div>
            <Label className="text-xs">Broker</Label>
            <Select value={brokerFilter} onValueChange={setBrokerFilter}>
              <SelectTrigger data-testid="filter-broker"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti</SelectItem>
                <SelectItem value="__none__">(senza broker)</SelectItem>
                {brokers.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Compagnia</Label>
            <Select value={companyFilter} onValueChange={setCompanyFilter}>
              <SelectTrigger data-testid="filter-company"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 flex-wrap pt-2 border-t border-slate-200">
          <div className="inline-flex rounded-md border border-slate-300 bg-white overflow-hidden">
            <button
              type="button"
              data-testid="group-broker"
              onClick={() => setGroupBy("broker")}
              className={`px-3 py-1.5 text-xs font-medium ${groupBy === "broker" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >
              Raggruppa per Broker
            </button>
            <button
              type="button"
              data-testid="group-company"
              onClick={() => setGroupBy("company")}
              className={`px-3 py-1.5 text-xs font-medium border-l border-slate-300 ${groupBy === "company" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >
              Raggruppa per Compagnia
            </button>
            <button
              type="button"
              data-testid="group-none"
              onClick={() => setGroupBy("none")}
              className={`px-3 py-1.5 text-xs font-medium border-l border-slate-300 ${groupBy === "none" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >
              Elenco piatto
            </button>
          </div>
          {data && (
            <div className="text-sm">
              <span className="text-slate-500 mr-2">Totale filtrato:</span>
              <span className="font-display font-bold text-emerald-800 tabular-nums text-lg" data-testid="total-amount">
                {totalLabel}
              </span>
            </div>
          )}
        </div>
      </div>

      {loading && <p className="text-center py-12 text-slate-400">Caricamento…</p>}

      {/* 🆕 22/07/2026 — Banner incassi disallineati con azione di ricalcolo bulk */}
      {data && !loading && (data.mismatch_count || 0) > 0 && (
        <div
          className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start justify-between gap-4"
          data-testid="mismatch-banner"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-display text-base font-semibold text-amber-900">
                {data.mismatch_count} incass{data.mismatch_count === 1 ? "o" : "i"} disallineat{data.mismatch_count === 1 ? "o" : "i"}
              </p>
              <p className="text-sm text-amber-800 mt-0.5">
                L&apos;importo salvato non corrisponde alla <span className="font-semibold">Provv. Ag. (fee incl.)</span> ricalcolata con la formula corrente.
                {" "}Delta totale: <span className="font-semibold tabular-nums">{formatCurrency(data.mismatch_delta || 0)}</span>.
              </p>
            </div>
          </div>
          <Button
            data-testid="recalc-all-mismatch-btn"
            onClick={recalcAllMismatch}
            className="bg-amber-600 hover:bg-amber-700 text-white shrink-0"
          >
            <RefreshCcw className="h-4 w-4 mr-1" /> Ricalcola tutti ({data.mismatch_count})
          </Button>
        </div>
      )}

      {data && !loading && data.rows.length === 0 && (
        <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
          <p className="text-slate-500">Nessuna provvigione incassata nel periodo/filtri selezionati.</p>
        </div>
      )}

      {data && !loading && data.rows.length > 0 && (
        <div className="space-y-6">
          {data.groups.map((g) => (
            <div key={g.key} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`group-${g.key}`}>
              <div className="flex items-center justify-between bg-emerald-50 border-b border-emerald-200 px-4 py-2.5">
                <h3 className="font-display text-base font-semibold text-emerald-900">{g.name}</h3>
                <div className="text-sm text-emerald-800 font-semibold tabular-nums">
                  {g.count} {g.count === 1 ? "riga" : "righe"} · {formatCurrency(g.total)}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[900px]">
                  <thead className="text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200 bg-slate-50">
                    <tr>
                      <th className="text-left px-3 py-2">Data incasso</th>
                      <th className="text-left px-3 py-2">Scadenza rata</th>
                      <th className="text-left px-3 py-2">Cliente</th>
                      <th className="text-left px-3 py-2">Polizza</th>
                      <th className="text-left px-3 py-2">Compagnia</th>
                      <th className="text-left px-3 py-2">Broker</th>
                      <th className="text-right px-3 py-2">Importo (€)</th>
                      <th className="text-left px-3 py-2">Note</th>
                      <th className="text-center px-3 py-2 print:hidden">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((r) => (
                      <tr key={`${r.policy_id}-${r.installment_date}`} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`row-${r.policy_id}-${r.installment_date}`}>
                        <td className="px-3 py-2 tabular-nums text-emerald-800 font-semibold">{formatDate(r.collected_at)}</td>
                        <td className="px-3 py-2 tabular-nums text-slate-700">{formatDate(r.installment_date)}</td>
                        <td className="px-3 py-2">{r.client_name}</td>
                        <td className="px-3 py-2 font-mono text-xs">
                          <button
                            type="button"
                            className="text-blue-600 hover:underline"
                            onClick={() => navigate(`/policies?focus=${r.policy_id}&return_to=${encodeURIComponent("/commissions/collected")}`)}
                            title="Apri polizza"
                          >
                            {r.policy_number || "—"}
                          </button>
                        </td>
                        <td className="px-3 py-2 text-slate-600">{r.company_name}</td>
                        <td className="px-3 py-2 text-slate-600">{r.broker_name}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-semibold text-emerald-800">
                          <div className="flex items-center justify-end gap-1.5">
                            <span>{formatCurrency(r.amount_collected)}</span>
                            {r.has_mismatch && (
                              <span
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-bold uppercase tracking-wide"
                                title={`Atteso: ${formatCurrency(r.expected_amount)} · Delta: ${(r.amount_diff >= 0 ? "+" : "")}${(r.amount_diff || 0).toFixed(2).replace(".", ",")} €\nRicalcola per correggere.`}
                                data-testid={`mismatch-badge-${r.policy_id}-${r.installment_date}`}
                              >
                                <AlertTriangle className="h-3 w-3" /> Δ
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">{r.note || ""}</td>
                        <td className="px-3 py-2 text-center print:hidden">
                          <div className="inline-flex items-center gap-1">
                            {r.has_mismatch && (
                              <button
                                type="button"
                                data-testid={`recalc-${r.policy_id}-${r.installment_date}`}
                                onClick={() => recalcOne(r.policy_id, r.installment_date)}
                                className="text-amber-600 hover:text-amber-800 hover:bg-amber-50 rounded p-1 transition-colors"
                                title={`Ricalcola importo (atteso: ${formatCurrency(r.expected_amount)})`}
                              >
                                <RefreshCcw className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              data-testid={`uncollect-${r.policy_id}-${r.installment_date}`}
                              onClick={() => uncollect(r.policy_id, r.installment_date)}
                              className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors"
                              title="Annulla incasso"
                            >
                              <XCircle className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {/* Totale generale */}
          <div className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-4 flex items-center justify-between" data-testid="total-summary">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">Totale cumulativo filtri attivi</p>
              <p className="text-sm text-emerald-800 mt-1">{data.count} provvigion{data.count === 1 ? "e" : "i"} incassate</p>
            </div>
            <p className="text-3xl font-display font-bold text-emerald-900 tabular-nums" data-testid="grand-total">
              {formatCurrency(data.total_amount)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
